import { Client, ErrorCode, type Room } from '@colyseus/sdk';
import {
  BattleRoomState,
  CLIENT_MSG,
  SERVER_MSG,
  type AchievementUnlockMessage,
  type BattleRoomState as BattleRoomStateType,
  type Direction,
  type GameInput,
  type GameModeId,
  type JoinInfo,
  type MatchResult,
  type MatchResetInfo,
  type PhaseEnvelope,
  type RejectionMessage,
  type TransitionMessage,
} from '@2k48/shared';
import { SyncedClock } from './SyncedClock';
import { PhaseTracker, type PhaseHandlers } from './phases';
import { metrics } from '../game/debug/metrics';

/**
 * The Colyseus connection, wrapped so the rest of the client never types a
 * message name or wires the clock by hand.
 *
 * Joining does two things in order that matter: connect, then complete the
 * clock handshake *before* handing the session back. Every phase the server
 * announces is an absolute timestamp, so a session handed over with an
 * unmeasured offset would schedule its first countdown against a clock that is
 * simply wrong.
 */

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? `ws://${location.hostname}:2567`;

export interface JoinOptions {
  playerId?: string;
  displayName?: string;
  /** Precomputed public queue bucket; the server still owns the actual ruleset. */
  ratingBucket?: string;
  /** Required only when joining an existing invitation room. */
  privateCode?: string;
  /** Which of the four bots to face. Ignored by every other mode. */
  difficulty?: string;
}

export interface SessionHandlers extends PhaseHandlers {
  onJoined?(info: JoinInfo): void;
  onTransitions?(message: TransitionMessage): void;
  onRejected?(message: RejectionMessage): void;
  onResult?(result: MatchResult): void;
  /** Arrives a beat after the result: the write path decides it, not the room. */
  onAchievements?(message: AchievementUnlockMessage): void;
  onReset?(info: MatchResetInfo): void;
  onState?(state: BattleRoomStateType): void;
  onDrop?(code: number, reason?: string): void;
  onReconnect?(): void;
  onLeave?(code: number): void;
  onError?(error: unknown): void;
}

export class MatchSession {
  readonly clock: SyncedClock;
  readonly phases: PhaseTracker;

  private inputSeq = 0;
  private info: JoinInfo | null = null;

  constructor(
    readonly room: Room,
    handlers: SessionHandlers,
  ) {
    this.clock = new SyncedClock((ping) => this.room.send(CLIENT_MSG.CLOCK_PING, ping));
    this.phases = new PhaseTracker(this.clock, handlers);

    room.onMessage(SERVER_MSG.CLOCK_PONG, (pong) => this.clock.onPong(pong));

    room.onMessage(SERVER_MSG.JOINED, (info: JoinInfo) => {
      this.info = info;
      handlers.onJoined?.(info);
    });

    room.onMessage(SERVER_MSG.PHASE, (envelope: PhaseEnvelope) => this.phases.set(envelope));

    room.onMessage(SERVER_MSG.TRANSITIONS, (message: TransitionMessage) =>
      handlers.onTransitions?.(message),
    );

    room.onMessage(SERVER_MSG.REJECTED, (message: RejectionMessage) =>
      handlers.onRejected?.(message),
    );

    room.onMessage(SERVER_MSG.RESULT, (result: MatchResult) => handlers.onResult?.(result));

    room.onMessage(SERVER_MSG.ACHIEVEMENTS, (message: AchievementUnlockMessage) =>
      handlers.onAchievements?.(message),
    );
    room.onMessage(SERVER_MSG.RESET, (reset: MatchResetInfo) => {
      if (this.info) this.info = { ...this.info, matchId: reset.matchId };
      this.inputSeq = 0;
      handlers.onReset?.(reset);
    });

    // Counted, not consumed: the board is driven by transition messages, not by
    // state patches. The rate is a diagnostic — a spike here with steady frames
    // means the room is chattier than it needs to be.
    room.onStateChange((state) => {
      metrics.recordPatch();
      handlers.onState?.(state as BattleRoomStateType);
    });

    room.onDrop((code, reason) => handlers.onDrop?.(code, reason));
    room.onReconnect(() => {
      // Mobile radios often reconnect through a different route. Re-measure
      // before trusting phase boundaries again.
      void this.clock.handshake().then(() => handlers.onReconnect?.());
    });

    room.onLeave((code) => handlers.onLeave?.(code));
    room.onError((code, message) => handlers.onError?.(new Error(`${code}: ${message}`)));
  }

  get seat(): number {
    return this.info?.seat ?? 0;
  }

  get matchId(): string | null {
    return this.info?.matchId ?? null;
  }

  get state(): BattleRoomStateType {
    return this.room.state as BattleRoomStateType;
  }

  get roomCode(): string {
    return this.info?.roomCode ?? this.state.roomCode ?? '';
  }

  ready(): void {
    this.room.send(CLIENT_MSG.READY, {});
  }

  /** Send a swipe. The server assigns the seat; what we put here is ignored. */
  sendDirection(direction: Direction): GameInput {
    this.inputSeq += 1;
    const input: GameInput = {
      kind: 'swipe',
      seat: this.seat,
      seq: this.inputSeq,
      at: this.clock.serverNow(),
      direction,
    };
    this.room.send(CLIENT_MSG.INPUT, input);
    return input;
  }

  forfeit(): void {
    this.room.send(CLIENT_MSG.FORFEIT, {});
  }

  rematch(): void {
    this.room.send(CLIENT_MSG.REMATCH, {});
  }

  async leave(): Promise<void> {
    this.destroy();
    await this.room.leave(true);
  }

  destroy(): void {
    this.phases.destroy();
    this.clock.destroy();
  }
}

let client: Client | null = null;

function colyseus(): Client {
  client ??= new Client(SERVER_URL);
  return client;
}

/**
 * Join (or create) a room for a mode, and return only once the clock is synced.
 */
export async function joinMatch(
  modeId: GameModeId,
  options: JoinOptions,
  handlers: SessionHandlers,
): Promise<MatchSession> {
  const matchmakingOptions =
    modeId === 'versus' ? { ...options, ratingBucket: options.ratingBucket ?? 'open' } : options;
  const room = await colyseus().joinOrCreate(modeId, matchmakingOptions, BattleRoomState);
  const session = new MatchSession(room, handlers);
  await session.clock.handshake();
  handlers.onState?.(session.state);
  return session;
}

const PRIVATE_VERSUS_ROOM = 'versus-private';

/** Create a four-character invitation room. */
export async function createPrivateMatch(
  options: JoinOptions,
  handlers: SessionHandlers,
): Promise<MatchSession> {
  const room = await colyseus().create(PRIVATE_VERSUS_ROOM, options, BattleRoomState);
  const match = new MatchSession(room, handlers);
  await match.clock.handshake();
  handlers.onState?.(match.state);
  return match;
}

/** Join the invitation room whose server-owned code matches `privateCode`. */
/**
 * Matchmaking codes that mean "that room is not joinable", as opposed to "the
 * server did not answer".
 *
 * The difference matters to the player and cannot be inferred from which call
 * failed: a private join fails identically whether the code was mistyped, the
 * lobby already closed, or the backend is down — and telling someone their code
 * expired when the server is simply unreachable sends them to retype a code
 * that was fine.
 */
const UNAVAILABLE_ROOM_CODES: readonly number[] = [
  ErrorCode.MATCHMAKE_INVALID_CRITERIA,
  ErrorCode.MATCHMAKE_INVALID_ROOM_ID,
  ErrorCode.MATCHMAKE_EXPIRED,
];

/** True when the room itself is gone or was never there. */
export function isRoomUnavailable(reason: unknown): boolean {
  const code = (reason as { code?: unknown } | null)?.code;
  return typeof code === 'number' && UNAVAILABLE_ROOM_CODES.includes(code);
}

export async function joinPrivateMatch(
  privateCode: string,
  options: JoinOptions,
  handlers: SessionHandlers,
): Promise<MatchSession> {
  const normalized = privateCode.trim().toUpperCase();
  const room = await colyseus().join(
    PRIVATE_VERSUS_ROOM,
    { ...options, privateCode: normalized },
    BattleRoomState,
  );
  const match = new MatchSession(room, handlers);
  await match.clock.handshake();
  handlers.onState?.(match.state);
  return match;
}

const API_URL = import.meta.env.VITE_API_URL ?? `http://${location.hostname}:2567`;

/**
 * The bearer token for authenticated requests, or null while playing as a
 * guest. Held here rather than passed per call so that adding an endpoint
 * cannot accidentally produce an unauthenticated variant of it — and so that
 * signing out is one assignment rather than a hunt through callers.
 */
let authToken: string | null = null;

export function setApiAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * A failed response, with the status and the parsed body kept intact.
 *
 * The auth endpoints answer with stable codes (`invalid_credentials`,
 * `username_taken`, …) precisely so the screens can decide the wording and
 * which field to mark. Flattening that into a message string on the way out
 * would throw away the only part a form actually needs.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    path: string,
  ) {
    super(`${status} for ${path}`);
    this.name = 'ApiError';
  }

  /** The server's error code, when the body carried one. */
  get code(): string | null {
    const error = (this.body as { error?: unknown } | null)?.error;
    return typeof error === 'string' ? error : null;
  }

  /** Which input the failure belongs to, when the server named one. */
  get field(): string | null {
    const field = (this.body as { field?: unknown } | null)?.field;
    return typeof field === 'string' ? field : null;
  }

  /** Seconds to wait, when the failure was a rate limit. */
  get retryAfter(): number | null {
    const seconds = (this.body as { retryAfter?: unknown } | null)?.retryAfter;
    return typeof seconds === 'number' ? seconds : null;
  }
}

/** Thin fetch helper for the REST side (rankings, history, profile, accounts). */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has('content-type')) headers.set('content-type', 'application/json');
  if (authToken) headers.set('authorization', `Bearer ${authToken}`);

  const response = await fetch(`${API_URL}/api${path}`, { ...init, headers });

  if (!response.ok) {
    // The body is optional and may not be JSON at all — a proxy failing in
    // front of this returns HTML. Parsing must never turn a 502 into a
    // `SyntaxError` that hides the status the caller needed to see.
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body, path);
  }

  // 204 is a real success with nothing to parse; logout returns one.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
